/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import isEqual from 'lodash/isEqual';

/**
 * Generic cache entry interface that can store any type of response
 */
export interface EarlyFetchCacheEntry<ResponseType, ParamsType> {
	response: Promise<ResponseType> | null;
	accessed: boolean;
	params: ParamsType | null; // Store the params used for the fetch
}

/**
 * Configuration options for the early fetch cache
 */
export interface EarlyFetchCacheOptions<ParamsType> {
	paramsEqualityFn?: (paramsA: ParamsType, paramsB: ParamsType) => boolean; // Optional custom params comparison function
}

/**
 * Creates a generic early fetch cache utility
 * This cache is designed to store responses for early data fetching before components mount
 * and can be reused for any type of request
 */
export class EarlyFetchCache<ResponseType, ParamsType> {
	private cache: EarlyFetchCacheEntry<ResponseType, ParamsType>;

	private paramsEqualityFn: (paramsA: ParamsType, paramsB: ParamsType) => boolean;

	constructor(options: EarlyFetchCacheOptions<ParamsType>) {
		this.paramsEqualityFn = options.paramsEqualityFn || this.defaultParamsEqual;
		this.cache = {
			response: null,
			accessed: false,
			params: null,
		};
	}

	/**
	 * Default parameter comparison function
	 * Deep compares two parameter objects to ensure they are equivalent
	 */
	private defaultParamsEqual(paramsA: ParamsType, paramsB: ParamsType): boolean {
		return isEqual(paramsA, paramsB);
	}

	/**
	 * Compares two parameter objects using the configured comparison function
	 */
	areParamsEqual(paramsA: ParamsType, paramsB: ParamsType): boolean {
		return this.paramsEqualityFn(paramsA, paramsB);
	}

	/**
	 * Clears the early fetch cache
	 * This function can be called to reset the cache when needed
	 */
	clearCache(): void {
		this.cache.response = null;
		this.cache.accessed = false;
		this.cache.params = null;
	}

	/**
	 * Sets the response in the cache with the associated parameters
	 */
	setCacheResponse(response: Promise<ResponseType>, params: ParamsType): void {
		this.cache.response = response;
		this.cache.params = params;
		this.cache.accessed = false;
	}

	/**
	 * Gets the cached response if it matches the provided parameters and marks it as accessed
	 * The cache will be automatically cleared after the promise resolves and has been accessed
	 */
	getCachedResponse(params: ParamsType): Promise<ResponseType> | null {
		// Check if there's no response
		if (!this.cache.response) {
			return null;
		}

		// Check if params match the cached params
		if (!this.cache.params || !this.areParamsEqual(this.cache.params, params)) {
			this.clearCache();
			return null;
		}

		// Set up promise settlement handler only on first access to avoid multiple handlers
		if (!this.cache.accessed) {
			// Mark as accessed
			this.cache.accessed = true;

			// Set up promise settlement handler to clear cache after promise settles
			this.cache.response.finally(() => {
				// Clear cache when promise has settled (resolved or rejected)
				this.clearCache();
			});
		}

		return this.cache.response;
	}
}

/**
 * Creates a type-safe early fetch cache instance with the specified options
 */
export function createEarlyFetchCache<ResponseType, ParamsType>(
	options: EarlyFetchCacheOptions<ParamsType>,
): EarlyFetchCache<ResponseType, ParamsType> {
	return new EarlyFetchCache<ResponseType, ParamsType>(options);
}
